/**
 * ChatInput Component
 *
 * Text input field for sending chat messages.
 * Supports Enter to send, Shift+Enter for newline, and push-to-talk (hold Space).
 * Phase 7: Frontend Integration
 *
 * @module ChatInput
 */

'use client';

import { useState, useCallback, type KeyboardEvent, useRef, useEffect } from 'react';
import { Box, Text, Textarea, IconButton, HStack } from '@axon/ui';
import { useTranslations, useLocale } from 'next-intl';
import { LuSend, LuLoader } from 'react-icons/lu';

import { useVoiceRecorder, useVoiceLabels, VoiceRecordButton } from '@/lib/voice';

import { useChatbotContext } from '../providers/ChatbotProvider';

// ============================================================================
// Constants
// ============================================================================

/** Maximum character length for input */
const MAX_INPUT_LENGTH = 2000;

/** Maximum rows before scrolling */
const MAX_ROWS = 4;

/** Hold duration threshold (ms) to distinguish tap from push-to-talk */
const PUSH_TO_TALK_THRESHOLD_MS = 400;

/** Minimum hold duration (ms) for audio to be useful — shorter recordings cause Whisper hallucinations */
const MIN_USEFUL_HOLD_MS = 1000;

// ============================================================================
// Component
// ============================================================================

/**
 * ChatInput component
 *
 * Text area with send button for composing messages.
 * Supports push-to-talk: hold Space (when textarea is empty) to record,
 * release to transcribe and auto-send.
 *
 * @example
 * <ChatInput />
 */
export function ChatInput() {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { sendMessage, session, isLoading } = useChatbotContext();
  const t = useTranslations('chatbot');
  const locale = useLocale();

  const isConnected = session?.isConnected ?? false;
  const isInputEmpty = input.trim().length === 0;
  const canSend = !isInputEmpty && isConnected && !isLoading;

  // Voice recorder — lifted here so ChatInput can trigger PTT via keyboard
  const voice = useVoiceRecorder({
    language: locale === 'tr' ? 'tr' : 'en',
    onTranscription: sendMessage,
  });
  const voiceLabels = useVoiceLabels(t);

  // Push-to-talk refs
  const spaceDownAtRef = useRef(0);
  /** When true, cancel recording as soon as it starts (race condition guard) */
  const pttPendingCancelRef = useRef(false);

  // Cancel recording if it starts after Space was already released (async getUserMedia race)
  useEffect(() => {
    if (voice.isRecording && pttPendingCancelRef.current) {
      pttPendingCancelRef.current = false;
      voice.cancelRecording();
    }
  }, [voice.isRecording, voice]);

  // a11y: land keyboard focus on the composer once the window opens and the
  // connection is ready. Guarded so a later reconnect doesn't steal focus.
  const didAutoFocusRef = useRef(false);
  useEffect(() => {
    if (isConnected && !didAutoFocusRef.current && textareaRef.current) {
      textareaRef.current.focus();
      didAutoFocusRef.current = true;
    }
  }, [isConnected]);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const lineHeight = parseInt(getComputedStyle(textareaRef.current).lineHeight);
      const maxHeight = lineHeight * MAX_ROWS;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [input]);

  /**
   * Handle send message
   */
  const handleSend = useCallback(async () => {
    if (!canSend) return;

    const content = input.trim();
    setInput('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    await sendMessage(content);
  }, [canSend, input, sendMessage]);

  /**
   * Handle keyboard events (keydown)
   * - Enter (no Shift): send message
   * - Space (empty textarea, voice idle, connected, not repeat): start recording immediately
   * - Escape (recording): cancel recording
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter without Shift sends the message
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
        return;
      }

      // Escape cancels active recording
      if (e.key === 'Escape' && voice.isRecording) {
        e.preventDefault();
        voice.cancelRecording();
        return;
      }

      // While a push-to-talk hold is active, swallow Space auto-repeat keydowns.
      // The start branch below only fires on the first (non-repeat) keydown, so
      // without this the OS key-repeat events fell through and leaked space
      // characters into the textarea for the whole duration of the hold.
      if (e.key === ' ' && spaceDownAtRef.current > 0) {
        e.preventDefault();
        return;
      }

      // Space push-to-talk: only when textarea empty, voice idle, connected, not auto-repeat
      if (e.key === ' ' && !e.repeat && isInputEmpty && voice.state === 'idle' && isConnected) {
        e.preventDefault();
        spaceDownAtRef.current = Date.now();
        pttPendingCancelRef.current = false;

        // Start recording immediately so audio is captured from the moment Space is pressed.
        // The threshold is only used on keyup to decide tap vs. hold.
        voice.startRecording();
      }
    },
    [handleSend, voice, isInputEmpty, isConnected]
  );

  /**
   * Handle keyboard events (keyup)
   * - Space short tap (<threshold): cancel recording + insert space
   * - Space long hold (>=threshold): stop recording → transcribe → auto-send
   */
  const handleKeyUp = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== ' ' || spaceDownAtRef.current === 0) return;

      const holdDuration = Date.now() - spaceDownAtRef.current;
      spaceDownAtRef.current = 0;

      if (holdDuration < PUSH_TO_TALK_THRESHOLD_MS) {
        // Short tap — cancel any in-progress recording and insert a space character
        voice.cancelRecording();
        // Guard: if startRecording is still async-pending, cancel when it resolves
        pttPendingCancelRef.current = true;
        setInput((prev) => prev + ' ');
      } else if (holdDuration < MIN_USEFUL_HOLD_MS) {
        // Held between 400ms-1000ms — too short for useful audio, cancel to prevent Whisper hallucinations
        voice.cancelRecording();
        pttPendingCancelRef.current = true;
      } else if (voice.isRecording) {
        // Long hold (>=1s) — stop recording → triggers transcription → auto-send
        voice.stopRecording();
      } else {
        // Held long enough but recording hasn't started yet (slow getUserMedia)
        // Cancel when it eventually starts — user already stopped speaking
        pttPendingCancelRef.current = true;
      }
    },
    [voice]
  );

  /**
   * Handle blur — clean up push-to-talk state when focus leaves textarea
   */
  const handleBlur = useCallback(() => {
    spaceDownAtRef.current = 0;
    pttPendingCancelRef.current = true;

    if (voice.isRecording) {
      voice.cancelRecording();
    }
  }, [voice]);

  /**
   * Handle input change with length validation
   */
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_INPUT_LENGTH) {
      setInput(value);
    }
  }, []);

  return (
    <Box
      padding="3"
      borderTop="1px solid"
      borderColor="border.default"
      bg="bg.default"
      flexShrink={0}
    >
      <HStack gap="2" align="flex-end">
        <Textarea
          data-testid="chat-input"
          ref={textareaRef}
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown as React.KeyboardEventHandler<HTMLTextAreaElement>}
          onKeyUp={handleKeyUp as React.KeyboardEventHandler<HTMLTextAreaElement>}
          onBlur={handleBlur}
          placeholder={isConnected ? t('inputPlaceholder') : t('connectingPlaceholder')}
          size="sm"
          resize="none"
          disabled={!isConnected}
          style={{
            flex: 1,
            minHeight: '2.5rem',
            maxHeight: '8rem',
            borderRadius: '8px',
            overflow: 'auto',
          }}
        />

        <VoiceRecordButton voice={voice} isConnected={isConnected} labels={voiceLabels} />

        <IconButton
          data-testid="chat-send"
          aria-label={t('sendMessage')}
          icon={isLoading ? <LuLoader className="animate-spin" size={18} /> : <LuSend size={18} />}
          onClick={handleSend}
          disabled={!canSend}
          size="md"
        />
      </HStack>

      {/* Push-to-talk hint — surfaced as helper text rather than baked into
          the placeholder (which truncated and read poorly on narrow widths) */}
      {isConnected && isInputEmpty && (
        <Text marginTop="1" fontSize="xs" color="fg.subtle">
          {t('voice.holdSpaceHint')}
        </Text>
      )}

      {/* Character count (only when approaching limit) */}
      {input.length > MAX_INPUT_LENGTH * 0.8 && (
        <Box marginTop="1" textAlign="right">
          <Box
            as="span"
            aria-live="polite"
            fontSize="xs"
            color={input.length >= MAX_INPUT_LENGTH ? 'fg.error' : 'fg.subtle'}
          >
            {input.length}/{MAX_INPUT_LENGTH}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ChatInput;
